import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const ProtectedRoute = ({ children, requireVerified = false, requireAdmin = false, requirePubNcnUnlocked = false }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/auth/login" state={{ from: location }} replace />;
    }

    if (requireVerified && !user.is_verified) {
        return <Navigate to="/auth/verify" replace />;
    }

    // finding D-02: /admin used to render behind the same bare <ProtectedRoute>
    // as every other page — this prop existed nowhere until now, so the
    // client-side admin gate was completely dead (mirroring finding C-04's
    // server-side gap). This is defence in depth only: the actual
    // authorization boundary is the backend's admin_required dependency —
    // this just stops a non-admin from momentarily seeing the admin shell
    // before an API call 403s.
    //
    // docs/futuregoals.md FG-6: was `user.plan_tier !== 'Endovelicus'` —
    // see Shell.jsx's adminLink for why that's wrong (it's not the same
    // check admin_required uses server-side). `is_admin` is computed there
    // from is_any_admin(), the actual backend authority.
    if (requireAdmin && !user.is_admin) {
        return <Navigate to="/hub" replace />;
    }

    // docs/futuregoals.md FG-6: same "defence in depth, frontend only" tier
    // as the admin gate above — the Hub and /admin stay reachable regardless
    // (the Hub is the launcher, /admin is the only place to undo the lock).
    // No backend route enforcement for this flag yet (see futuregoals.md's
    // FG-6 open questions) — a real incident should also disable the
    // relevant server-side actions if this proves insufficient on its own.
    if (requirePubNcnUnlocked && user?.system_flags?.pubncn_locked && !user.is_system_admin) {
        return <Navigate to="/hub" replace />;
    }

    return children;
};

export default ProtectedRoute;
